import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { UserPlus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewJoining = ({ user, onLogout }) => {
  const [joinings, setJoinings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => { fetchJoinings(); }, []);

  const fetchJoinings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/new-joinings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJoinings(res.data.joinings || []);
    } catch (e) {
      toast.error('Failed to fetch');
    } finally { setLoading(false); }
  };

  const createJoining = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/admin/new-joining`, {
        name, designation, message
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Welcome announcement created! It will show on TV Display.');
      setShowDialog(false);
      setName(''); setDesignation(''); setMessage('');
      fetchJoinings();
    } catch (e) {
      toast.error('Failed to create');
    }
  };

  const toggleJoining = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/new-joining/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJoinings();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteJoining = async (id, name) => {
    if (!window.confirm(`Remove welcome for ${name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/new-joining/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed');
      fetchJoinings();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="flex min-h-screen bg-slate-900" data-testid="new-joining-page">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 overflow-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <UserPlus className="w-8 h-8 text-purple-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">New Joining Announcements</h1>
                <p className="text-slate-400 text-sm">Welcome new team members on the TV Display</p>
              </div>
            </div>
            <Button onClick={() => setShowDialog(true)} className="bg-purple-500 hover:bg-purple-600">
              <UserPlus className="w-4 h-4 mr-2" /> Announce New Joining
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : joinings.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="py-16 text-center">
                <UserPlus className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No active joining announcements</p>
                <p className="text-slate-500 text-sm mt-1">Create one to welcome a new team member on the TV Display</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {joinings.map(j => (
                <Card key={j.id} className={`border ${j.is_active ? 'bg-slate-800 border-purple-500/30' : 'bg-slate-800/50 border-slate-700 opacity-60'}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <span className="text-2xl font-bold text-white">{j.name.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{j.name}</h3>
                          {j.designation && <p className="text-amber-400 text-sm">{j.designation}</p>}
                          <p className="text-slate-400 text-sm mt-1 max-w-xl truncate">{j.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${j.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/30 text-slate-500'}`}>
                          {j.is_active ? 'Active on TV' : 'Inactive'}
                        </span>
                        <button onClick={() => toggleJoining(j.id)} className="text-slate-400 hover:text-white p-1" title={j.is_active ? 'Deactivate' : 'Activate'}>
                          {j.is_active ? <ToggleRight className="w-6 h-6 text-emerald-400" /> : <ToggleLeft className="w-6 h-6" />}
                        </button>
                        <button onClick={() => deleteJoining(j.id, j.name)} className="text-red-400 hover:text-red-300 p-1" title="Delete">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-400" /> Announce New Joining
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={createJoining} className="space-y-4">
            <div>
              <Label className="text-slate-300">Full Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Rahul Sharma" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Designation / Role</Label>
              <Input value={designation} onChange={e => setDesignation(e.target.value)} placeholder="e.g., Sales Executive" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Welcome Message (optional - auto-generated if empty)</Label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Welcome to the Creative Infotech family! Wishing you a successful journey ahead."
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-md p-3 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-purple-500 hover:bg-purple-600">Announce on TV</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewJoining;
